import {
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type RefObject,
  useContext,
  useState,
} from "react";

import { FileContext } from "@styled/file_context";

import { useFilePicker } from "./use_file_picker";

/**
 * Drag/drop + click-to-browse behavior for the Add Records drop zones
 * (`PriorDrop`).
 *
 * Composes `useFilePicker` for the hidden input + OS picker, and layers on
 * drag state, drop handlers, and keyboard activation. Callers spread
 * `containerProps` onto their outer element and `inputProps` onto the hidden
 * input; files flow into the enclosing `FileManager` via `FileContext.onSelect`.
 */
export function useFileDropZone(options: { multiple?: boolean } = {}): {
  dragging: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  containerProps: {
    role: "button";
    tabIndex: 0;
    onClick: () => void;
    onKeyDown: (event: KeyboardEvent) => void;
    onDragOver: (event: DragEvent) => void;
    onDragLeave: (event: DragEvent) => void;
    onDrop: (event: DragEvent) => void;
  };
  inputProps: {
    type: "file";
    className: "sr-only";
    multiple: boolean;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  };
} {
  const { onSelect } = useContext(FileContext);
  const { inputRef, openPicker, inputProps } = useFilePicker(options);
  const [dragging, setDragging] = useState(false);

  const handleDragOver = (event: DragEvent) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (event: DragEvent) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (event: DragEvent) => {
    setDragging(false);
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer.files;
    if (files.length > 0) onSelect([...files]);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openPicker();
    }
  };

  return {
    dragging,
    inputRef,
    containerProps: {
      role: "button",
      tabIndex: 0,
      onClick: openPicker,
      onKeyDown: handleKeyDown,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
    inputProps,
  };
}
